import React, { useState } from "react";

export default function FilterPanel({ 
  filters, 
  setFilters, 
  isOpen, 
  onClose,
  categories = [],
  conditions = ['excellent', 'good', 'fair', 'poor'],
  ageRanges = ['0-3M', '3-6M', '6-12M', '12-24M', '2-4Y', '4Y+', 'All Ages'],
  availableTags = ['moving-out', 'give-away', 'urgent', 'bundle', 'new', 'negotiable', 'pickup-only']
}) {
  const [tempFilters, setTempFilters] = useState(filters);
  const [customAgeRange, setCustomAgeRange] = useState("");

  const handleApplyFilters = () => {
    const finalFilters = {
      ...tempFilters,
      ageRange: tempFilters.ageRange === "custom" ? customAgeRange : tempFilters.ageRange
    };
    setFilters(finalFilters);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      priceRange: [0, 1000],
      maxDistance: 50,
    category: '',
    condition: '',
    ageRange: '',
    tags: [],
    sortBy: 'newest'
    };
    setTempFilters(resetFilters);
    setFilters(resetFilters);
    onClose();
  };

  const handlePriceChange = (index, value) => {
    const newPriceRange = [...tempFilters.priceRange];
    newPriceRange[index] = parseInt(value);
    setTempFilters({ ...tempFilters, priceRange: newPriceRange });
  };

  const handleDistanceChange = (value) => {
    setTempFilters({ ...tempFilters, maxDistance: parseInt(value) });
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}

      {/* Filter Panel */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-xl z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
            <button
              onClick={onClose}
              className="touch-target text-slate-500 hover:text-slate-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Filter Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Price Range */}
            <div>
              <label className="form-label">Price Range</label>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-sm text-slate-600 mb-1 block">Min</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={tempFilters.priceRange[0]}
                      onChange={(e) => handlePriceChange(0, e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm text-slate-600 mb-1 block">Max</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="1000"
                      value={tempFilters.priceRange[1]}
                      onChange={(e) => handlePriceChange(1, e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Price Range Slider */}
                <div className="relative">
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    step="10"
                    value={tempFilters.priceRange[1]}
                    onChange={(e) => handlePriceChange(1, e.target.value)}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>$0</span>
                    <span>${tempFilters.priceRange[1]}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Distance */}
            <div>
              <label className="form-label">Maximum Distance</label>
              <div className="space-y-3">
                <input
                  type="range"
                  min="1"
                  max="100"
                  step="1"
                  value={tempFilters.maxDistance}
                  onChange={(e) => handleDistanceChange(e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-slate-600">
                  <span>1 km</span>
                  <span className="font-medium">{tempFilters.maxDistance} km</span>
                  <span>100 km</span>
                </div>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={tempFilters.category}
                onChange={(e) => setTempFilters({ ...tempFilters, category: e.target.value })}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Condition */}
            <div>
              <label className="form-label">Condition</label>
              <select
                className="form-select"
                value={tempFilters.condition}
                onChange={(e) => setTempFilters({ ...tempFilters, condition: e.target.value })}
              >
                <option value="">All Conditions</option>
                {conditions.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition.charAt(0).toUpperCase() + condition.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Age Range */}
            <div>
              <label className="form-label">Age Range</label>
              <select
                className="form-select"
                value={tempFilters.ageRange}
                onChange={(e) => setTempFilters({ ...tempFilters, ageRange: e.target.value })}
              >
                <option value="">All Ages</option>
                {ageRanges.map((ageRange) => (
                  <option key={ageRange} value={ageRange}>
                    {ageRange === '0-3M' && '👶 0-3 months (Newborn)'}
                    {ageRange === '3-6M' && '🍼 3-6 months'}
                    {ageRange === '6-12M' && '🧸 6-12 months'}
                    {ageRange === '12-24M' && '🚶 12-24 months (Toddler)'}
                    {ageRange === '2-4Y' && '🏃 2-4 years (Preschool)'}
                    {ageRange === '4Y+' && '🎒 4+ years (School age)'}
                    {ageRange === 'All Ages' && '🌟 All Ages'}
                  </option>
                ))}
                <option value="custom">✏️ Custom Range</option>
              </select>
              
              {tempFilters.ageRange === "custom" && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={customAgeRange}
                    onChange={(e) => setCustomAgeRange(e.target.value)}
                    className="form-input"
                    placeholder="e.g., 4-6M, 18-24M, 3-5Y"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter a custom age range (e.g., 4-6M for 4-6 months)
                  </p>
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="form-label">Tags</label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'moving-out', label: 'Moving Out', emoji: '📦' },
                    { id: 'give-away', label: 'Give Away', emoji: '🎁' },
                    { id: 'urgent', label: 'Urgent Sale', emoji: '⚡' },
                    { id: 'bundle', label: 'Bundle Deal', emoji: '📦' },
                    { id: 'new', label: 'Brand New', emoji: '✨' },
                    { id: 'negotiable', label: 'Negotiable', emoji: '💰' },
                    { id: 'pickup-only', label: 'Pickup Only', emoji: '🚚' }
                  ].map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => {
                        const currentTags = tempFilters.tags || [];
                        if (currentTags.includes(tag.id)) {
                          setTempFilters({ ...tempFilters, tags: currentTags.filter(t => t !== tag.id) });
                        } else {
                          setTempFilters({ ...tempFilters, tags: [...currentTags, tag.id] });
                        }
                      }}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${
                        (tempFilters.tags || []).includes(tag.id)
                          ? 'bg-blue-100 text-blue-800 border border-blue-300'
                          : 'bg-slate-100 text-slate-700 border border-transparent hover:bg-slate-200'
                      }`}
                    >
                      <span>{tag.emoji}</span>
                      <span>{tag.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="form-label">Sort By</label>
              <select
                className="form-select"
                value={tempFilters.sortBy}
                onChange={(e) => setTempFilters({ ...tempFilters, sortBy: e.target.value })}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="distance">Distance: Nearest First</option>
              </select>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 space-y-3">
            <button
              onClick={handleApplyFilters}
              className="w-full btn-primary"
            >
              Apply Filters
            </button>
            <button
              onClick={handleResetFilters}
              className="w-full btn-secondary"
            >
              Reset All
            </button>
          </div>
        </div>
      </div>

      {/* Custom Slider Styles */}
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </>
  );
}
